import { useEffect, useState } from 'react';
import { createContainer } from 'unstated-next';
import useAppState from '../../app/use-app-state';
import { useConfigControllerFindAll } from '../../v2/api/generated/configs/configs';
import {
  CONFIG_TYPE_LANGUAGE,
  DEFAULT_LANGUAGE_CONFIG_KEY,
} from '../../v2/pages/organization/configurations/language/constants';
import type {
  Config,
  ConfigApiResponse,
} from '../../v2/pages/organization/configurations/language/types';
import AuthContainer from '../../v2/shared/containers/AuthContainer';

const PatientOrgContainer = createContainer(() => {
  const [organizationId, setOrganizationId] = useState<number | null>(null);
  const [orgName, setOrgName] = useState<string>('');
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // Get authentication state
  const { isLogin } = AuthContainer.useContainer();

  // Get current locale and setLocale function from app state
  const [
    {
      appStateCookie: { localeKey: currentLocale },
    },
    { setLocale },
  ] = useAppState();

  // Fetch organization config to get default language
  const { data: configData, isError: isConfigError } = useConfigControllerFindAll({
    query: {
      enabled: !!organizationId && isLogin,
    },
  });

  // Apply organization language from config
  useEffect(() => {
    if (!organizationId) return;

    // If API error occurred, fallback to English
    if (isConfigError) {
      console.warn('Failed to fetch organization config, falling back to English');
      // if (currentLocale !== 'english') {
      //   setLocale('english');
      // }
      return;
    }

    if (!configData) return;

    // Handle nested API response structure
    const response = configData as ConfigApiResponse;
    let configList: Config[] = [];

    if (response?.data?.data) {
      configList = response.data.data;
    } else if (response?.data && Array.isArray(response.data)) {
      configList = response.data as unknown as Config[];
    } else if (Array.isArray(configData)) {
      configList = configData as unknown as Config[];
    }

    const languageConfig = configList.find(
      (config: Config) =>
        config.configType === CONFIG_TYPE_LANGUAGE &&
        config.key === DEFAULT_LANGUAGE_CONFIG_KEY &&
        config.organizationId === organizationId,
    );

    if (languageConfig?.value) {
      // Map backend language codes to frontend locale keys
      const languageMap: Record<string, string> = {
        en: 'english',
        'en-us': 'english',
        'en-gb': 'english',
        ko: 'korean',
        ja: 'japanese',
      };

      const localeKey = languageMap[languageConfig.value] || languageMap.en;

      // Only update if different from current locale
      // if (localeKey !== currentLocale) {
      //   setLocale(localeKey);
      // }
    }
  }, [configData, organizationId, currentLocale, setLocale, isConfigError]);

  return {
    organizationId,
    orgName,
    logoUrl,
    isLoading,
    setOrganizationId,
    setOrgName,
    setLogoUrl,
    setIsLoading,
  };
});

export default PatientOrgContainer;
